import { css, html, LitElement } from 'lit'

/*
As this object is currently implemented, it is coupled closely with a Rails turbo stream backed server side UI for showing existing files and their delete buttons.
At one point the path was pursued to have the rendering and deletion logic all in this JS, but that got messy really fast. Coupling this with Rails gives the best of both worlds.

This component handles showing the upload input and a progress bar as the file is uploaded. It can handle removing itself in the case where multiple files are not supported.
- Written by Josh :)
 */
export default class Uploader extends LitElement {
  static UPLOAD_STATES = { IDLE: 'idle', PENDING: 'pending', UPLOADING: 'uploading', SAVING: 'saving' }

  static properties = {
    allowedFileExtensions: { type: Array, attribute: true, reflect: true },
    multiple: { type: Boolean, attribute: true, reflect: true },
    invalidFileExtensions: { type: Array, state: true },
    dataTransfer: { type: Object, state: true },
    filenames: { type: Array, state: true },
    uploadProgress: { type: Number, state: true },
    uploadState: { type: String, state: true },
    singleFileUploaded: { type: Boolean, state: true },
  }

  constructor() {
    super()

    this.allowedFileExtensions = undefined
    this.multiple = false
    this.invalidFileExtensions = []
    this.dataTransfer = new DataTransfer()
    this.filenames = []
    this.uploadProgress = 0
    this.uploadState = Uploader.UPLOAD_STATES.IDLE
    this.singleFileUploaded = false
  }

  connectedCallback() {
    super.connectedCallback()
    const input = this._fileInput()
    input.addEventListener('input', this._onUpload.bind(this))

    input.addEventListener('direct-upload:start', () => {
      this.uploadState = Uploader.UPLOAD_STATES.UPLOADING
      this.uploadProgress = 0
    })

    input.addEventListener('direct-upload:progress', (event) => {
      this.uploadProgress = event.detail.progress
    })

    input.addEventListener('direct-upload:end', () => {
      this.uploadState = Uploader.UPLOAD_STATES.SAVING
      this.uploadProgress = 100

      setTimeout(() => {
        if (this.uploadState === Uploader.UPLOAD_STATES.SAVING) {
          this.uploadState = Uploader.UPLOAD_STATES.IDLE
        }

        if (!this.multiple) {
          this.singleFileUploaded = true
        } else {
          this._resetFileInput()
        }
      }, 1500)
    })

    input.addEventListener('direct-upload:error', (event) => {
      this.uploadState = Uploader.UPLOAD_STATES.IDLE
      this.uploadProgress = 0
      event.preventDefault()
      this.invalidFileExtensions = [`Upload failed: ${event.detail.error}`]
    })
  }

  _fileInput() { return this.querySelector('input[type="file"]') }

  reset() {
    this.singleFileUploaded = false
    this.uploadState = Uploader.UPLOAD_STATES.IDLE
    this._resetFileInput()
  }

  _resetFileInput() {
    const input = this._fileInput()
    input.disabled = false
    this.dataTransfer = new DataTransfer()
    this.filenames = []
    input.files = this.dataTransfer.files
    this.requestUpdate()
  }

  _onUpload(event) {
    this.invalidFileExtensions = []
    const files = [...event.target.files]
    event.target.value = ''
    files.forEach(file => this._addFile(file))
  }

  _onClick(_event) {
    this._fileInput().click()
  }

  _onDragover(event) {
    event.preventDefault()

    event.currentTarget.classList.add('upload-container--dragover')
  }

  _onDragleave(event) {
    event.preventDefault()

    event.currentTarget.classList.remove('upload-container--dragover')
  }

  _onDrop(event) {
    event.preventDefault()

    this.invalidFileExtensions = []
    event.currentTarget.classList.remove('upload-container--dragover')
    const dataTransferItems = [...event.dataTransfer.items]
    dataTransferItems.forEach(item => this._addEntryToFiles(item.webkitGetAsEntry()))
  }

  _addEntryToFiles(entry) {
    if (entry?.isFile) {
      entry.file(file => this._addFile(file, entry.fullPath.substring(1)))
    } else if (entry?.isDirectory) {
      const dirReader = entry.createReader()
      dirReader.readEntries(entries => {
        entries.forEach(entry => this._addEntryToFiles(entry))
      })
    }
  }

  _addFile(file, fullPath = undefined) {
    const splitFilename = file.name.split('.')
    const fileExtension = splitFilename.pop()
    const allowedFileExtensions = this.allowedFileExtensions.map(extension => extension.toLowerCase())

    if (Boolean(this.allowedFileExtensions) && !allowedFileExtensions.includes(fileExtension.toLowerCase())) {
      this._fileInput().files = this.dataTransfer.files
      return this.invalidFileExtensions.push(fileExtension)
    }

    this.dataTransfer.items.add(file)
    this.filenames.push(fullPath ?? file.name)
    const input = this._fileInput()
    input.files = this.dataTransfer.files

    this.uploadState = Uploader.UPLOAD_STATES.PENDING
    this.uploadProgress = 0

    const changeEvent = new Event('change', { bubbles: true, composed: true })
    input.dispatchEvent(changeEvent)

    this.requestUpdate()
  }

  _renderUploadError() {
    if (this.invalidFileExtensions.length === 0) return

    return html`
      <div class="upload-error">Invalid file types rejected: '${this.invalidFileExtensions.join("', '")}'</div>
    `
  }

  _renderProgressBar() {
    const { IDLE, PENDING, UPLOADING, SAVING } = Uploader.UPLOAD_STATES
    if (this.uploadState === IDLE) return

    const indeterminate = this.uploadState === PENDING
    const label = {
      [PENDING]: 'Preparing upload...',
      [UPLOADING]: `Uploading... ${Math.round(this.uploadProgress)}%`,
      [SAVING]: 'Saved',
    }[this.uploadState]

    return html`
      <div class="upload-progress">
        <div class="upload-progress__bar">
          <div class="upload-progress__fill ${indeterminate ? 'upload-progress__fill--indeterminate' : ''}"
               style="width: ${indeterminate ? '100%' : `${this.uploadProgress}%`}"></div>
        </div>
        <span class="upload-progress__text">${label}</span>
      </div>
    `
  }

  render() {
    return html`
      <slot name="label" class="files-label">Files To Upload</slot>
      ${this._renderUploadError()}
      ${this._renderProgressBar()}
      ${this.singleFileUploaded ? '' : html`
        <div class="upload-container" @click=${this._onClick} @drop=${this._onDrop} @dragover=${this._onDragover} @dragleave=${this._onDragleave}>
          <slot class="drop-zone-text" name="drop-zone-text">Click to choose or drag and drop files</slot>
          <div>(${this.allowedFileExtensions.map(fileExtension => `.${fileExtension}`).join(', ')})</div>
          <slot name="input" class="files-input"></slot>
        </div>
      `}
      <slot name="errors" class="files-errors"></slot>
    `
  }

  static styles = css`
    .upload-container {
      height: var(--op-space-5x-large);
      display: flex;
      flex-direction: column;
      justify-content: safe center;
      align-items: safe center;
      cursor: pointer;
      padding: var(--op-space-small);
      position: relative;
      background-color: var(--op-color-background);
      color: var(--op-color-primary-on-plus-eight-alt);
      border-radius: var(--op-radius-medium);
      border: 2px dashed var(--op-color-neutral-plus-six);

      .files-input {
        display: none;
      }

      &.upload-container--dragover, &:hover {
        background-color: var(--op-color-primary-plus-eight);
        border: 2px dashed var(--op-color-primary-plus-five);
      }
    }

    .upload-container * {
      pointer-events: none;
    }

    @keyframes indeterminate {
      0% { opacity: 0.4; }
      50% { opacity: 1; }
      100% { opacity: 0.4; }
    }

    .upload-progress {
      display: flex;
      align-items: center;
      gap: var(--op-space-x-small);
      padding: var(--op-space-x-small) 0;

      .upload-progress__bar {
        flex: 1;
        height: 6px;
        background-color: var(--op-color-neutral-plus-seven);
        border-radius: 3px;
        overflow: hidden;

        .upload-progress__fill {
          height: 100%;
          background-color: var(--op-color-primary-base);
          border-radius: 3px;
          transition: width 0.15s ease-out;

          &.upload-progress__fill--indeterminate {
            animation: indeterminate 1.5s ease-in-out infinite;
          }
        }
      }

      .upload-progress__text {
        font-size: var(--op-font-x-small);
        color: var(--op-color-neutral-on-plus-two);
        white-space: nowrap;
      }
    }

    .files-label {
      color: var(--op-color-on-background);
      font-size: var(--op-font-x-small);
    }

    .files-errors {
      color: var(--op-color-alerts-danger-base);
      font-size: var(--op-font-x-small);
    }


    .upload-error {
      color: var(--op-color-alerts-danger-base)
    }

    .drop-zone-text {
      font-size: var(--op-font-small);
      text-align: center;
    }
  `
}
